import React from 'react';
import { useTheme } from '@mui/material';
import { WidgetWrapper, useWidgetEditable } from '@brandclub/bc-widgets';
import type { DataPdpProductInfoPartType, PdpProductInfoData } from '../../types/conciergeToolTypes';
import { PdpProductInfoPartV2 } from './PdpProductInfoPartV2';
import {
  PDP_PRODUCT_CARD_WIDGET_ID,
  PDP_PRODUCT_CARD_DISPLAY_NAME,
  pdpProductCardDefaultState,
  pdpProductCardEditFields,
  buildPdpProductCardStyles,
  type PdpProductCardState,
} from './pdpProductInfoCardConfig';

/**
 * Entry for the `data-pdp-product-info` card. Concierge is BrandApp V2-only, so
 * product cards always use the Product V2 URL behavior.
 *
 * Styling is brand-editable through the shared theme editor: `useWidgetEditable`
 * registers the fields and `WidgetWrapper` renders the pencil. Outside edit mode
 * `WidgetWrapper` returns its children untouched, so the production DOM is
 * unchanged.
 */
export function PdpProductInfoPart({ part }: { part: DataPdpProductInfoPartType }) {
  const theme = useTheme();
  const {
    state,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    studioLockedKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<PdpProductCardState>(
    PDP_PRODUCT_CARD_WIDGET_ID,
    pdpProductCardDefaultState,
    pdpProductCardEditFields,
  );

  // The shared `data-${string}` part union widens `part` to `never` under the
  // Extract alias, so narrow to the concrete payload shape here.
  const data = (part as { data: PdpProductInfoData }).data;
  if (!data?.stacklineSku) return null;

  const styles = buildPdpProductCardStyles(state, resolveColor, {
    textPrimary: theme.palette.text.primary,
  });

  return (
    <WidgetWrapper
      widgetId={PDP_PRODUCT_CARD_WIDGET_ID}
      displayName={PDP_PRODUCT_CARD_DISPLAY_NAME}
      editFields={pdpProductCardEditFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      studioLockedKeys={studioLockedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      <PdpProductInfoPartV2 data={data} styles={styles} />
    </WidgetWrapper>
  );
}
